import type { ReactNode } from 'react';

import { useT } from '@/i18n/LocaleProvider';

export type SiteNavProps = {
  graphLabel?: string;
  homeSlug?: string;
  onNavigateHome: () => void;
  onAllPages: () => void;
  onCategories: () => void;
  onMap: () => void;
  onRandom: () => void;
  onRecentChanges: () => void;
  onReports?: () => void;
  onStats?: () => void;
  onWatchlist?: () => void;
  onAdminUsers?: () => void;
  onToggleGraph: () => void;
  graphVisible: boolean;
  tocSlot?: ReactNode;
  children?: ReactNode;
};

type SiteNavLink = {
  href: string;
  label: string;
  active: boolean;
  onClick: () => void;
};

export function SiteNav({
  graphLabel,
  onNavigateHome,
  onAllPages,
  onCategories,
  onMap,
  onRandom,
  onRecentChanges,
  onReports,
  onStats,
  onWatchlist,
  onAdminUsers,
  onToggleGraph,
  graphVisible,
  tocSlot,
  children,
}: SiteNavProps) {
  const t = useT();
  const displayedGraphLabel = graphLabel ?? t('nav_relations');
  const pathname = currentPathname();
  const links: SiteNavLink[] = [
    { href: '/', label: t('nav_home'), active: pathname === '/', onClick: onNavigateHome },
    {
      href: '/pages',
      label: t('nav_all_pages'),
      active: pathname === '/pages',
      onClick: onAllPages,
    },
    {
      href: '/categories',
      label: t('nav_categories'),
      active: pathname === '/categories' || pathname.startsWith('/categories/'),
      onClick: onCategories,
    },
    {
      href: '/map',
      label: t('nav_map'),
      active: pathname === '/map',
      onClick: onMap,
    },
    {
      href: '/pages',
      label: t('nav_random'),
      active: false,
      onClick: onRandom,
    },
    {
      href: '/recent',
      label: t('nav_recent_changes'),
      active: pathname === '/recent',
      onClick: onRecentChanges,
    },
    ...(onReports
      ? [
          {
            href: '/reports',
            label: t('nav_reports'),
            active: pathname === '/reports',
            onClick: onReports,
          },
        ]
      : []),
    ...(onStats
      ? [
          {
            href: '/stats',
            label: t('nav_stats'),
            active: pathname === '/stats',
            onClick: onStats,
          },
        ]
      : []),
    ...(onWatchlist
      ? [
          {
            href: '/watchlist',
            label: t('nav_watchlist'),
            active: pathname === '/watchlist',
            onClick: onWatchlist,
          },
        ]
      : []),
    ...(onAdminUsers
      ? [
          {
            href: '/admin/users',
            label: t('nav_admin_users'),
            active: pathname === '/admin/users',
            onClick: onAdminUsers,
          },
        ]
      : []),
  ];

  return (
    <nav className="wiki-rail" aria-label={t('nav_aria_label')}>
      <div className="wiki-rail-nav">
        {links.map((link) => (
          <a
            aria-current={link.active ? 'page' : undefined}
            className={link.active ? 'is-active' : undefined}
            href={link.href}
            key={link.label}
            onClick={(event) => {
              event.preventDefault();
              link.onClick();
            }}
          >
            {link.label}
          </a>
        ))}

        <button aria-pressed={graphVisible} type="button" onClick={onToggleGraph}>
          {displayedGraphLabel}
        </button>
      </div>

      {children}
      {tocSlot}
    </nav>
  );
}

function currentPathname(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.pathname;
}
